import { FeatureDefinition } from "../../api/vehicleFeatures";
import { RequirementSummary } from "../VehicleFeatures/RequirementSummary";
/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useState, type FC } from "react";
import { IOrder, IRouteItem } from "../../types";
import { useTranslation } from "react-i18next";
import { Customer } from "../../models";
import {
  getPickupAndDeliveryStops,
  getCargoTotals,
} from "../../helpers/orderData";
import { Spinner, Textarea, Tooltip } from "flowbite-react";
import { createSearchParams, useNavigate } from "react-router-dom";
import {
  HiCheck,
  HiOutlineUser,
  HiOutlineCalendar,
  HiOutlineScale,
  HiOutlineCube,
  HiOutlineArrowUp,
  HiOutlineArrowDown,
  HiOutlineClock,
} from "react-icons/hi";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useUserSession } from "../../context/UserContext";
import { IOrderProduct, Timestamp } from "../../types";
import { createOrder as createOrderApi, checkPricing } from "../../api";
import { Button } from "@components";
import { useWorkspaceData, useDataCache } from "@data";
import {
  isDriverCompanyVatRegistered,
  isTimePriceDescription,
} from "../../utils/pricing";
import { inputSurface, textBody, tableRowHover } from "@design-system";

export interface OrderSummaryProp {
  vehicleDefinitions?: FeatureDefinition[];
  order: IOrder;
  close: () => void;
}

// Define types for the pricing details we want to store
interface PriceItem {
  description?: string;
  discountInPercent?: number;
  price?: number;
  quantity?: number;
  unit?: string;
  vat?: number;
}

interface PriceAmounts {
  totalAmount?: number;
  totalAmountWithoutVat?: number;
  totalVatAmount?: number;
}

interface PricingDetails {
  amounts: PriceAmounts;
  items: PriceItem[];
}

// Define the overall response structure from checkPricing more accurately
interface CheckPricingResponse {
  data?: {
    amounts?: PriceAmounts; // Use the defined interface
    items?: PriceItem[]; // Use the defined interface
  };
  error?: any;
  success?: boolean;
}

export const OrderSummary: FC<OrderSummaryProp> = (props) => {
  const { t, i18n } = useTranslation(["orders", "common"]);
  const [comment, setComment] = useState(props.order.comment || "");
  const [projectNumber, setProjectNumber] = useState(
    props.order.projectNumber || "",
  );
  const [loading, setIsLoading] = useState(false);
  const [create, setCreate] = useState(false);
  const [createError, setCreateError] = useState(false);
  const [fullOrder, setFullOrder] = useState(props.order);
  const [priceEstimate, setPriceEstimate] = useState<number | null>(null);
  const [pricingDetails, setPricingDetails] = useState<PricingDetails | null>(
    null,
  );
  const [isPriceFetching, setIsPriceFetching] = useState(false);
  const [isCompanyVatRegistered, setIsCompanyVatRegistered] = useState<
    boolean | null
  >(null);

  // Address validation error state
  const [addressValidationError, setAddressValidationError] = useState<{
    message: string;
    stopNumber: number | null;
    address: string;
  } | null>(null);

  const { activeWorkspace } = useWorkspace();
  const { userSession } = useUserSession();
  const { invalidateOrders } = useDataCache();
  const navigate = useNavigate();

  // Drivers and companies come from the centrally-loaded data store.
  const { drivers, companies, loading: workspaceLoading } = useWorkspaceData();
  const isDriversLoading = workspaceLoading.drivers;
  const isCompaniesLoading = workspaceLoading.companies;

  // Determine Company VAT status based on driverId via shared util
  useEffect(() => {
    if (!isDriversLoading && !isCompaniesLoading) {
      const flag = isDriverCompanyVatRegistered(
        fullOrder.driverId,
        drivers,
        companies,
      );
      setIsCompanyVatRegistered(flag);
    }
  }, [
    fullOrder.driverId,
    drivers,
    companies,
    isDriversLoading,
    isCompaniesLoading,
  ]);

  // Fetch price estimate
  useEffect(() => {
    const getPriceEstimate = async () => {
      // Ensure user, workspace, order data, and VAT status are ready
      if (
        !userSession?.token.accessToken ||
        !activeWorkspace?.workspaceId ||
        !fullOrder.cargo ||
        fullOrder.cargo.length === 0 ||
        !fullOrder.route ||
        fullOrder.route.length === 0 ||
        isCompanyVatRegistered === null // Wait until VAT status is determined
      ) {
        return;
      }

      setIsPriceFetching(true);
      setPriceEstimate(null);
      setPricingDetails(null);

      try {
        // Pass isCompanyVatRegistered to checkPricing
        const response: CheckPricingResponse = await checkPricing(
          userSession.token.accessToken,
          activeWorkspace.workspaceId,
          fullOrder,
          isCompanyVatRegistered, // Pass the flag here
        );

        if (
          !response?.success ||
          (response?.error && Object.keys(response.error).length > 0)
        ) {
          const errorMsg =
            response?.error?.message || "Failed to fetch price estimate";
          console.error(
            "Error from checkPricing:",
            response?.error || errorMsg,
          );
        } else if (response?.data?.amounts && response.data.items) {
          const receivedAmounts = response.data.amounts;
          const receivedItems = response.data.items;

          setPriceEstimate(receivedAmounts.totalAmount ?? null);
          setPricingDetails({
            amounts: receivedAmounts,
            items: receivedItems,
          });
        } else {
          console.log(
            "Unexpected response format from checkPricing:",
            response,
          );
        }
      } catch (error: any) {
        console.error("Error in getPriceEstimate catch block:", error);

        // Handle authentication errors specifically
        if (error?.response?.status === 401) {
          console.warn(
            "Authentication error - token may have expired. Please refresh and try again.",
          );
          // Could add a toast notification here or redirect to login
        }
      } finally {
        setIsPriceFetching(false);
      }
    };

    getPriceEstimate();
    // Add isCompanyVatRegistered to dependency array
  }, [fullOrder, userSession, activeWorkspace, isCompanyVatRegistered]);

  // Handle order creation
  useEffect(() => {
    const createOrder = async () => {
      // ** Check token validity before proceeding **
      const token = userSession?.token.accessToken;
      if (!token || !activeWorkspace?.workspaceId) {
        console.error("Missing token or workspace ID before creating order.");
        setIsLoading(false);
        return;
      }

      // Optional: Decode token to check expiry (requires jwt-decode library)
      // try {
      //   const decodedToken = jwt_decode<{ exp: number }>(token);
      //   const currentTime = Date.now() / 1000;
      //   if (decodedToken.exp < currentTime) {
      //     console.warn("Token appears to be expired before API call.");
      //     alert("Your session may have expired. Please try refreshing or logging in again.");
      //     // Optionally try forcing a refresh here
      //     // await userSession?.token.getIdToken(true);
      //   }
      // } catch (e) {
      //   console.error("Error decoding token:", e);
      // }

      // Construct the payload with correct date format for createOrder
      // Ensure all required fields from IRouteItem (except stopDate) are present
      const orderPayload = {
        ...fullOrder,
        workspaceId: activeWorkspace?.workspaceId || "",
        comment: comment || "",
        projectNumber: projectNumber || "",
        route: fullOrder.route.map((route) => ({
          // Map required IRouteItem fields
          location: route.location,
          stopNumber: route.stopNumber,
          contact: route.contact,
          timing: route.timing,
          status: route.status,
          isPickup: route.isPickup,
          // stopDate is formatted below
          // Explicitly omit fields not strictly part of IRouteItem if they cause issues
          // deliveryComment: route.deliveryComment,
          stopDate: route.stopDate
            ? Timestamp.toFormattedDate(route.stopDate, "YYYY-MM-DD")
            : null,
        })),
        // Type assertion for the payload sent to createOrderApi
        // Adjust Omit if other fields from IRouteItem are not needed/causing issues
      } as Omit<IOrder, "route"> & {
        route: Array<
          Omit<IRouteItem, "stopDate" | "routeId" | "orderId"> & {
            stopDate: string | null;
          }
        >;
      };

      setIsLoading(true);
      setCreateError(false);
      try {
        const res = await createOrderApi(
          userSession?.token.accessToken ?? "",
          activeWorkspace?.workspaceId ?? "",
          orderPayload,
        );

        // Corrected error check: Check for success flag or non-empty error object
        if (
          !res?.success ||
          (res?.error && Object.keys(res.error).length > 0)
        ) {
          setCreateError(true);
          // Handle address validation errors specifically
          if (
            res?.error?.response?.data?.data?.error?.type ===
            "AddressValidationError"
          ) {
            setAddressValidationError({
              message: t("orders:address_not_found", {
                stopNumber: res.error.response.data.data.error.stop_number,
                address:
                  res.error.response.data.data.error.address ||
                  "Unknown address",
              }),
              stopNumber: res.error.response.data.data.error.stop_number,
              address:
                res.error.response.data.data.error.address || "Unknown address",
            });
          }

          setIsLoading(false);
          // Check for successful response data structure
        } else if (res?.data?.id) {
          let createdOrderId = res.data.id;
          invalidateOrders();
          navigate({
            pathname: "/daily-planner",
            search: createSearchParams({
              driver: fullOrder.driverId || "",
              date: fullOrder.route[0]?.stopDate
                ? Timestamp.toFormattedDate(
                    fullOrder.route[0].stopDate,
                    "YYYY-MM-DD",
                  )
                : new Date().toISOString().split("T")[0],
              showSuccessPopup: "true",
              orderId: createdOrderId,
            }).toString(),
          });
        } else {
          console.error(
            "Unexpected successful response format from createOrder:",
            res,
          );
          setIsLoading(false);
        }
      } catch (error: any) {
        setCreateError(true);
        console.error("Failed to create order (catch block):", error);
        setIsLoading(false);
      }
    };

    if (create) {
      setCreate(false);
      createOrder();
    }
  }, [
    fullOrder,
    create,
    navigate,
    activeWorkspace,
    userSession,
    invalidateOrders,
    comment,
  ]);

  // Sync local state with incoming props
  useEffect(() => {
    setFullOrder(props.order);
    setComment(props.order.comment || "");
    setProjectNumber(props.order.projectNumber || "");
  }, [props.order]);

  const handleCreateOrderClick = () => {
    setFullOrder((prevOrder) => ({
      ...prevOrder,
      comment: comment,
      projectNumber: projectNumber,
    }));
    setCreate(true);
  };

  // Format price with currency
  const formatPrice = (price: number | null | undefined) => {
    if (price === null || price === undefined) return "";
    return new Intl.NumberFormat("nb-NO", {
      style: "currency",
      currency: "NOK",
    }).format(price);
  };

  // --- Calculation and Display Logic ---
  const order = fullOrder;
  const customerName =
    Customer.getCustomerName(order.customer) || t("common:unknown_customer");

  const stopTypes = () => {
    const { pickups, deliveries } = getPickupAndDeliveryStops(order);
    return { pickupStops: pickups, deliveryStops: deliveries };
  };

  const calculateTotalVolume = (products?: IOrderProduct[]) => {
    if (!products) return "0";
    const total = getCargoTotals(products).totalVolume;
    return total > 0 ? total.toFixed(2) : "0";
  };

  const calculateTotalWeight = (products?: IOrderProduct[]) => {
    if (!products) return "0";
    const total = getCargoTotals(products).totalWeight;
    return total > 0 ? total.toFixed(1) : "0";
  };
  // --- End Calculation and Display Logic ---

  // --- JSX ---
  return (
    <div className="flex flex-col max-h-full bg-surface-modal">
      {/* Match the modal shell so the content area is not a mismatched grey */}
      <div className="space-y-4 mb-16 px-2 bg-surface-modal flex-grow overflow-y-auto">
        {/* Address Validation Error Display */}
        {addressValidationError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  {t("orders:address_validation_error")}
                </h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  <p>{addressValidationError.message}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {createError && (
          <p role="alert" className="mb-4 text-sm text-danger">
            {t("workspace:features.createError")}
          </p>
        )}
        {/* Combined & Compacted Top Section */}
        <div className="bg-gray-50 dark:bg-surface p-4 rounded-lg shadow-sm">
          {props.vehicleDefinitions && (
            <RequirementSummary
              definitions={props.vehicleDefinitions}
              requirements={fullOrder.vehicleRequirements || []}
              className="mb-4 text-sm text-secondary"
            />
          )}
          {/* Top section with 2-column grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Left column - Icons, metrics and customer */}
            <div>
              {/* Icons and metrics row */}
              <div className="flex justify-start gap-8 mb-4">
                {/* Date */}
                <div className="flex flex-col items-center">
                  <Tooltip content={t("create.driver_selection.summary.date")}>
                    <HiOutlineCalendar
                      size={24}
                      className="text-brand dark:text-brand mb-2"
                    />
                  </Tooltip>
                  <span className="text-[14px] text-secondary whitespace-nowrap">
                    {order.route &&
                    order.route.length > 0 &&
                    order.route[0]?.stopDate
                      ? Timestamp.toDisplayDate(order.route[0].stopDate)
                      : t("common:unknown_date", "Unknown date")}
                  </span>
                </div>

                {/* Weight */}
                <div className="flex flex-col items-center">
                  <Tooltip
                    content={t("create.driver_selection.summary.total_weight")}
                  >
                    <HiOutlineScale
                      size={24}
                      className="text-brand dark:text-brand mb-2"
                    />
                  </Tooltip>
                  <span className="text-[14px] text-secondary whitespace-nowrap">
                    {calculateTotalWeight(order.cargo)} kg
                  </span>
                </div>

                {/* Volume */}
                <div className="flex flex-col items-center">
                  <Tooltip
                    content={t("create.driver_selection.summary.total_volume")}
                  >
                    <HiOutlineCube
                      size={24}
                      className="text-brand dark:text-brand mb-2"
                    />
                  </Tooltip>
                  <span className="text-[14px] text-secondary whitespace-nowrap">
                    {calculateTotalVolume(order.cargo)} m<sup>3</sup>
                  </span>
                </div>
              </div>

              {/* Customer below icons */}
              <div className="flex items-center pl-1 mt-1">
                <HiOutlineUser className="text-success mr-2 h-5 w-5 flex-shrink-0" />
                <span
                  className="text-[14px] text-secondary truncate"
                  title={customerName || "N/A"}
                >
                  {customerName || "N/A"}
                </span>
              </div>
            </div>

            {/* Right column - Route details */}
            <div className="flex flex-col">
              {/* Route title */}
              <div className="flex items-center mb-2">
                <span className="text-[14px] text-secondary">
                  {t("create.driver_selection.summary.route", "Rute")}
                </span>
              </div>

              {/* Route Details - Vertical layout */}
              <div className="flex flex-col space-y-1 ml-1 border-l-2 border-l-gray-200 dark:border-l-gray-700 pl-3">
                {/* Pickup locations */}
                <div className="flex flex-col space-y-1 mb-1">
                  {stopTypes().pickupStops.map((r, index) => (
                    <div
                      key={`pickup-${r?.stopNumber || index}`}
                      className="flex items-center"
                    >
                      <div className="flex items-center">
                        <div className="mr-2 w-5 h-5 flex items-center justify-center rounded-full bg-brand-muted dark:bg-lgb-blue-400/20">
                          <HiOutlineArrowUp className="h-3 w-3 text-brand dark:text-brand" />
                        </div>
                        <span
                          className="text-[14px] text-secondary truncate"
                          title={
                            r.location?.addressLine ||
                            t("orders:create.route_selection.no_address")
                          }
                        >
                          {r.location?.addressLine ||
                            t("orders:create.route_selection.no_address")}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Delivery locations */}
                <div className="flex flex-col space-y-1">
                  {stopTypes().deliveryStops.map((r, index) => (
                    <div
                      key={`delivery-${r?.stopNumber || index}`}
                      className="flex items-center"
                    >
                      <div className="flex items-center">
                        <div className="mr-2 w-5 h-5 flex items-center justify-center rounded-full bg-success-muted dark:bg-lgb-green-400/20">
                          <HiOutlineArrowDown className="h-3 w-3 text-lgb-green-500 dark:text-success" />
                        </div>
                        <span
                          className="text-[14px] text-secondary truncate"
                          title={
                            r.location?.addressLine ||
                            t("orders:create.route_selection.no_address")
                          }
                        >
                          {r.location?.addressLine ||
                            t("orders:create.route_selection.no_address")}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Price and Comment Section (Single Column Layout) */}
        <div className="grid grid-cols-1 gap-4">
          <div className="bg-gray-50 dark:bg-surface p-3 rounded-lg shadow-sm">
            <h3 className="font-medium text-base mb-3 text-text">
              {t("common:price_estimate", "Price Estimate")}
            </h3>

            {/* Loading state */}
            {(isPriceFetching || isDriversLoading || isCompaniesLoading) && (
              <div className="flex items-center space-x-2 py-3">
                <Spinner size="sm" />
                <span className="text-gray-600 dark:text-gray-300 text-sm">
                  {t("common:loading", "Laster...")}
                </span>
              </div>
            )}

            {/* Price Details & Comment Section - Render if pricingDetails exist */}
            {!isPriceFetching &&
              !isDriversLoading &&
              !isCompaniesLoading &&
              pricingDetails && (
                <div>
                  {/* Price Items Layout */}
                  <div className="overflow-x-auto mb-4">
                    {" "}
                    {/* Added margin bottom */}
                    <table className="w-full table-auto text-sm">
                      {" "}
                      {/* Apply text-sm to table */}
                      <thead>
                        <tr className="border-b-2 border-border">
                          <th className="text-left py-2 px-2 font-medium text-gray-800 dark:text-gray-200 w-4/12 text-sm">
                            {" "}
                            {/* Reduced padding */}
                            {t("common:description", "Description")}
                          </th>
                          <th className="text-center py-2 px-2 font-medium text-gray-800 dark:text-gray-200 w-2/12 text-sm">
                            {t("common:quantity", "Quantity")}
                          </th>
                          <th className="text-right py-2 px-2 font-medium text-gray-800 dark:text-gray-200 w-2/12 text-sm">
                            {t("common:price", "Price")}
                          </th>
                          <th className="text-right py-2 px-2 font-medium text-gray-800 dark:text-gray-200 w-2/12 text-sm">
                            {t("common:vat", "VAT")}
                          </th>
                          <th className="text-right py-2 px-2 font-medium text-gray-800 dark:text-gray-200 w-2/12 text-sm">
                            {t("common:total", "Total")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const rows: JSX.Element[] = [];
                          const items = pricingDetails.items;
                          for (let i = 0; i < items.length; i++) {
                            const item = items[i];
                            const description = item?.description || "";
                            if (isTimePriceDescription(description)) {
                              const perUnit = item.unit || "";
                              const localizedPrefix = t(
                                "common:time_price_button",
                                "Time price",
                              );
                              const displayDescription = description.replace(
                                /^\s*Tidspris/i,
                                localizedPrefix,
                              );
                              rows.push(
                                <tr
                                  key={`time-${i}`}
                                  className="border-b border-border-light bg-surface-muted/50"
                                >
                                  <td className="py-3 px-2 text-secondary font-medium text-sm">
                                    <div className="flex items-center">
                                      <HiOutlineClock className="mr-2 h-4 w-4 flex-shrink-0" />
                                      {displayDescription}
                                    </div>
                                  </td>
                                  <td className="py-3 px-2 text-center text-muted text-sm">
                                    -
                                  </td>
                                  <td className="py-3 px-2 text-right text-secondary text-sm whitespace-nowrap">
                                    {formatPrice(item.price)}
                                    {perUnit ? ` / ${perUnit}` : ""}{" "}
                                    {t("common:excl_vat", "eks. mva")}
                                  </td>
                                  <td className="py-3 px-2 text-right text-muted text-sm">
                                    {isCompanyVatRegistered &&
                                    item.vat !== undefined &&
                                    item.vat > 0
                                      ? `${item.vat}%`
                                      : "-"}
                                  </td>
                                  <td className="py-3 px-2 text-right text-secondary font-medium text-sm">
                                    {t(
                                      "common:calculated_label",
                                      "Blir beregnet",
                                    )}
                                  </td>
                                </tr>,
                              );
                              continue;
                            }

                            const displayUnit =
                              i18n.language.startsWith("no") &&
                              item.unit === "pcs"
                                ? "stk"
                                : item.unit || "";
                            const lineSubtotal =
                              (item.price || 0) * (item.quantity || 1);
                            const vatRateNum = (item.vat || 0) / 100;
                            const lineVatAmount = lineSubtotal * vatRateNum;
                            const lineTotalWithVat =
                              lineSubtotal + lineVatAmount;
                            rows.push(
                              <tr
                                key={`row-${i}`}
                                className={`border-b border-border-light ${tableRowHover}`}
                              >
                                <td className="py-3 px-2 text-secondary font-medium text-sm">
                                  {item.description || "N/A"}
                                </td>
                                <td className="py-3 px-2 text-center text-muted text-sm">
                                  {item.quantity !== undefined
                                    ? `${item.quantity > 0 ? item.quantity : ""} ${displayUnit}`
                                    : "-"}
                                </td>
                                <td className="py-3 px-2 text-right text-secondary text-sm">
                                  {formatPrice(item.price)}
                                  {item.unit && item.unit !== "pcs"
                                    ? ` / ${displayUnit}`
                                    : ""}
                                </td>
                                <td className="py-3 px-2 text-right text-muted text-sm">
                                  {isCompanyVatRegistered &&
                                  item.vat !== undefined &&
                                  item.vat > 0
                                    ? `${item.vat}%`
                                    : "-"}
                                </td>
                                <td className="py-3 px-2 text-right text-secondary font-medium text-sm">
                                  {isCompanyVatRegistered
                                    ? formatPrice(lineTotalWithVat)
                                    : formatPrice(lineSubtotal)}
                                </td>
                              </tr>,
                            );
                          }
                          return rows;
                        })()}
                      </tbody>
                    </table>
                  </div>

                  {/* Nested Grid for Comment, Project Number & Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    {" "}
                    {/* Removed border-t, pt-4. Added mt-4 for spacing */}
                    {/* Column 1: Comment and Project Number */}
                    <div className="space-y-4">
                      {/* Comment Section */}
                      <div>
                        <h3 className="font-medium text-base mb-2 text-text">
                          {t("create.comment_title")}
                        </h3>
                        <Textarea
                          style={{ height: "80px" }}
                          className={`${inputSurface} box-border w-full focus:ring-brand focus:border-brand`}
                          id="comment"
                          name="comment"
                          value={comment}
                          placeholder={t("create.comment_ph")}
                          onChange={(e) => setComment(e.target.value)}
                        />
                      </div>

                      {/* Project Number Section */}
                      <div>
                        <h3 className="font-medium text-base mb-2 text-text">
                          {t("create.project_number_title")}
                        </h3>
                        <input
                          type="text"
                          className={`${inputSurface} box-border w-full focus:ring-brand focus:border-brand p-2.5`}
                          id="projectNumber"
                          name="projectNumber"
                          value={projectNumber}
                          placeholder={t("create.project_number_ph")}
                          onChange={(e) => setProjectNumber(e.target.value)}
                        />
                      </div>
                    </div>
                    {/* Column 2: Summary Totals - Apply text-sm to parent, override for total */}
                    <div className="w-full text-sm">
                      <div className="grid grid-cols-2 gap-x-5 gap-y-2">
                        {(() => {
                          const hasVat =
                            (pricingDetails.amounts.totalVatAmount || 0) > 0;

                          return (
                            <>
                              {/* Subtotal excluding VAT */}
                              <div className="text-gray-600 dark:text-gray-300 whitespace-nowrap text-left">
                                {hasVat
                                  ? t("common:total_excl_vat", "Total eks. MVA")
                                  : t("common:subtotal", "Subtotal")}
                                :
                              </div>
                              <div className="font-medium text-gray-800 dark:text-gray-100 text-right">
                                {formatPrice(
                                  pricingDetails.amounts.totalAmountWithoutVat,
                                )}
                              </div>

                              {/* VAT amount if present */}
                              {hasVat && (
                                <>
                                  <div className="text-gray-600 dark:text-gray-300 whitespace-nowrap text-left">
                                    {t("common:vat", "MVA")}:
                                  </div>
                                  <div className="font-medium text-gray-800 dark:text-gray-100 text-right">
                                    {formatPrice(
                                      pricingDetails.amounts.totalVatAmount,
                                    )}
                                  </div>
                                </>
                              )}

                              {/* Total including VAT */}
                              <div
                                className={`${textBody} whitespace-nowrap text-left font-bold pt-3 border-t border-border mt-1 text-base`}
                              >
                                {hasVat
                                  ? t(
                                      "common:total_incl_vat",
                                      "Total inkl. MVA",
                                    )
                                  : t("common:total", "Total")}
                                :
                              </div>
                              <div
                                className={`${textBody} text-right font-bold pt-3 border-t border-border mt-1 text-base`}
                              >
                                {formatPrice(
                                  pricingDetails.amounts.totalAmount,
                                )}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                      {/* Time price disclaimer if any time item present */}
                      {pricingDetails.items.some((it) =>
                        isTimePriceDescription(it.description),
                      ) && (
                        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md text-yellow-800 mt-3">
                          {t("orders:create.time_price_disclaimer")}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            {/* Placeholder if no price details and not loading */}
            {!isPriceFetching &&
              !isDriversLoading &&
              !isCompaniesLoading &&
              !pricingDetails && (
                <div className="text-center py-4 text-muted text-sm">
                  {t(
                    "common:no_price_available",
                    "Price details not available.",
                  )}
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Knapper Section - Button container background should match the modal shell */}
      <div className="flex justify-end gap-3 pt-3 border-t border-border bg-surface-modal w-full py-3 absolute bottom-0 left-0 right-0 px-5">
        <Button
          variant="secondary"
          onClick={() => props.close()}
          size="md"
          className="px-5 py-2.5"
        >
          {t("common:close")}
        </Button>

        {!loading ? (
          <Button
            variant="success"
            onClick={handleCreateOrderClick}
            icon={<HiCheck />}
            size="md"
            className="px-5 py-2.5"
            disabled={!!addressValidationError}
          >
            {t("create.create_order_button")}
          </Button>
        ) : (
          <Button
            variant="success"
            onClick={() => {}}
            disabled={true}
            size="md"
            className="px-5 py-2.5"
          >
            <Spinner size="sm" />
          </Button>
        )}
      </div>
    </div>
  );
};
